// src/pages/Fruits.jsx — FINAL FIXED
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Grid, Card, CardContent,
  CardActions, TextField, InputAdornment, Paper, Chip,
  IconButton, Button, FormControl, Select, MenuItem,
  Drawer, CircularProgress, Pagination,
  Tooltip, alpha, useTheme, useMediaQuery, Stack,
} from '@mui/material';
import {
  Search, FilterList, Close, Favorite, FavoriteBorder,
  Star, Public, GridView, ViewList, Refresh,
  ArrowRightAlt, Agriculture,
} from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { fruitsAPI } from '../services/api';

const _BACKEND = (process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:5000').replace(/\/$/, '');

// ✅ FINAL image resolver — always uses image_path (raw filename) to build correct URL
const getImageSrc = (fruit) => {
  if (!fruit) return null;
  const rawPath = fruit.image_path || fruit.image_url;
  if (!rawPath) return null;
  if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) return rawPath;
  const filename = rawPath.split('/').pop().split('\\').pop();
  if (!filename) return null;
  return `${_BACKEND}/static/uploads/fruits/${filename}`;
};

const getCategoryEmoji = (category) => {
  const map = {
    Apple: '🍎', Pineapple: '🍍', Mango: '🥭',
    Orange: '🍊', Grapes: '🍇', Banana: '🍌',
    Berries: '🫐', Melon: '🍈', Citrus: '🍋',
    Tropical: '🥥', Exotic: '🥝',
  };
  return map[category] || '🍎';
};

const getPrice = (fruit) => parseFloat(fruit?.price_per_unit || 0);

// ── Fruit Card ─────────────────────────────────────────────────────────────────
const FruitCard = ({ fruit, viewMode, onWishlist, isWishlisted }) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const imgSrc = getImageSrc(fruit);
  const price  = getPrice(fruit);

  const ImageBox = ({ height = 200, fontSize = '3.5rem' }) => (
    <Box sx={{ position: 'relative', overflow: 'hidden', height, bgcolor: 'grey.100' }}>
      {imgSrc && !imgError ? (
        <Box
          className="fruit-img"
          component="img"
          src={imgSrc}
          alt={fruit.name}
          onError={() => setImgError(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }}
        />
      ) : (
        <Box sx={{
          width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)',
          fontSize,
        }}>
          {getCategoryEmoji(fruit.category)}
        </Box>
      )}
    </Box>
  );

  // ── List view ──
  if (viewMode === 'list') {
    return (
      <motion.div layout initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
        <Card sx={{
          display: 'flex', borderRadius: 3, overflow: 'hidden',
          border: '1px solid', borderColor: 'divider', transition: 'all 0.3s',
          '&:hover': { boxShadow: '0 12px 40px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
        }} elevation={0}>
          <Box sx={{ width: 160, height: 140, flexShrink: 0, overflow: 'hidden', bgcolor: 'grey.100' }}>
            {imgSrc && !imgError ? (
              <Box component="img" src={imgSrc} alt={fruit.name}
                onError={() => setImgError(true)}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Box sx={{
                width: '100%', height: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)', fontSize: '2.5rem',
              }}>
                {getCategoryEmoji(fruit.category)}
              </Box>
            )}
          </Box>

          <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6" fontWeight="bold">{fruit.name}</Typography>
                {fruit.category && (
                  <Chip label={fruit.category} size="small" variant="outlined" color="primary" />
                )}
              </Box>
              {Array.isArray(fruit.origins) && fruit.origins.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Public sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {fruit.origins.join(', ')}
                  </Typography>
                </Box>
              )}
              {Array.isArray(fruit.varieties) && fruit.varieties.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {fruit.varieties.slice(0, 3).map((v, i) => (
                    <Chip key={i} label={v} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ textAlign: 'right', minWidth: 150 }}>
              {price > 0 ? (
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  ${price.toFixed(2)}
                  <Typography component="span" variant="caption" color="text.secondary">/unit</Typography>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Price on request
                </Typography>
              )}
              {fruit.moq && (
                <Typography variant="caption" color="text.secondary" display="block">
                  MOQ: {fruit.moq}
                </Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
                <IconButton size="small" onClick={() => onWishlist(fruit.id)}>
                  {isWishlisted
                    ? <Favorite sx={{ color: 'error.main', fontSize: 18 }} />
                    : <FavoriteBorder sx={{ fontSize: 18 }} />}
                </IconButton>
                <Button variant="contained" size="small" endIcon={<ArrowRightAlt />}
                  onClick={() => navigate(`/fruits/${fruit.id}`)} sx={{ borderRadius: 2 }}>
                  View
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // ── Grid view ──
  return (
    <motion.div layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }} style={{ height: '100%' }}>
      <Card sx={{
        height: '100%', display: 'flex', flexDirection: 'column',
        borderRadius: 3, overflow: 'hidden',
        border: '1px solid', borderColor: 'divider', transition: 'all 0.3s',
        '&:hover': {
          boxShadow: '0 16px 48px rgba(0,0,0,0.14)',
          transform: 'translateY(-4px)',
          '& .fruit-img': { transform: 'scale(1.06)' },
        },
      }} elevation={0}>

        {/* Image */}
        <Box sx={{ position: 'relative', overflow: 'hidden', height: 200, bgcolor: 'grey.100' }}>
          {imgSrc && !imgError ? (
            <Box
              className="fruit-img"
              component="img"
              src={imgSrc}
              alt={fruit.name}
              onError={() => setImgError(true)}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }}
            />
          ) : (
            <Box sx={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)', fontSize: '3.5rem',
            }}>
              {getCategoryEmoji(fruit.category)}
            </Box>
          )}

          {/* Wishlist button */}
          <IconButton size="small" onClick={() => onWishlist(fruit.id)} sx={{
            position: 'absolute', top: 8, right: 8,
            bgcolor: 'white', boxShadow: 2, '&:hover': { bgcolor: 'white' },
          }}>
            {isWishlisted
              ? <Favorite sx={{ color: 'error.main', fontSize: 18 }} />
              : <FavoriteBorder sx={{ fontSize: 18 }} />}
          </IconButton>

          {/* Origin badge */}
          {Array.isArray(fruit.origins) && fruit.origins[0] && (
            <Chip
              label={fruit.origins[0]} size="small"
              icon={<Public sx={{ fontSize: '12px !important' }} />}
              sx={{
                position: 'absolute', bottom: 8, left: 8,
                bgcolor: alpha('#000', 0.6), color: 'white',
                backdropFilter: 'blur(4px)',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          )}
        </Box>

        {/* Content */}
        <CardContent sx={{ flex: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ maxWidth: '65%', lineHeight: 1.2 }}>
              {fruit.name}
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              {price > 0 ? (
                <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                  ${price.toFixed(2)}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">On request</Typography>
              )}
            </Box>
          </Box>

          {fruit.category && (
            <Chip label={fruit.category} size="small" variant="outlined" color="primary"
              sx={{ mb: 1, height: 20, fontSize: '0.7rem' }} />
          )}

          {Array.isArray(fruit.varieties) && fruit.varieties.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {fruit.varieties.slice(0, 2).map((v, i) => (
                <Chip key={i} label={v} size="small"
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'grey.100' }} />
              ))}
              {fruit.varieties.length > 2 && (
                <Chip label={`+${fruit.varieties.length - 2}`} size="small"
                  sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
            </Box>
          )}

          {Array.isArray(fruit.grades) && fruit.grades.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Star sx={{ fontSize: 14, color: '#ff9800' }} />
              <Typography variant="caption" color="text.secondary">
                {fruit.grades.join(' · ')}
              </Typography>
            </Box>
          )}

          {fruit.moq && (
            <Typography variant="caption" color="text.secondary" display="block">
              MOQ: {fruit.moq}
            </Typography>
          )}
        </CardContent>

        <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button fullWidth variant="contained" endIcon={<ArrowRightAlt />}
            onClick={() => navigate(`/fruits/${fruit.id}`)} sx={{ borderRadius: 2 }}>
            View Details
          </Button>
        </CardActions>
      </Card>
    </motion.div>
  );
};

// ── Main Fruits Page ───────────────────────────────────────────────────────────
const Fruits = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [fruits, setFruits]                 = useState([]);
  const [filteredFruits, setFilteredFruits] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [searchTerm, setSearchTerm]         = useState(searchParams.get('search') || '');
  const [viewMode, setViewMode]             = useState('grid');
  const [filterDrawer, setFilterDrawer]     = useState(false);
  const [wishlist, setWishlist]             = useState([]);
  const [page, setPage]                     = useState(1);
  const [categories, setCategories]         = useState([]);
  const [origins, setOrigins]               = useState([]);
  const [filters, setFilters]               = useState({
    category: 'all', origin: 'all', sortBy: 'newest', priceRange: [0, 500],
  });

  const itemsPerPage = 12;

  const fetchFruits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fruitsAPI.getAll();
      const raw  = response?.data?.fruits || response?.data || [];
      const list = Array.isArray(raw) ? raw : [];
      setFruits(list);

      const cats  = [...new Set(list.map((f) => f.category).filter(Boolean))];
      const origs = [...new Set(
        list.flatMap((f) => Array.isArray(f.origins) ? f.origins : []).filter(Boolean)
      )];
      setCategories(cats);
      setOrigins(origs);

      const prices = list.map(f => getPrice(f)).filter(p => p > 0);
      if (prices.length > 0)
        setFilters(prev => ({ ...prev, priceRange: [0, Math.ceil(Math.max(...prices) * 1.2)] }));
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load fruits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFruits(); }, [fetchFruits]);

  // Filter & sort
  useEffect(() => {
    let result = [...fruits];

    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      result = result.filter(f =>
        f.name?.toLowerCase().includes(s) ||
        f.category?.toLowerCase().includes(s) ||
        f.description?.toLowerCase().includes(s) ||
        (Array.isArray(f.varieties) && f.varieties.some(v => v.toLowerCase().includes(s))) ||
        (Array.isArray(f.origins)   && f.origins.some(o => o.toLowerCase().includes(s)))
      );
    }

    if (filters.category !== 'all')
      result = result.filter(f => f.category === filters.category);
    if (filters.origin !== 'all')
      result = result.filter(f => Array.isArray(f.origins) && f.origins.includes(filters.origin));

    const [minP, maxP] = filters.priceRange;
    result = result.filter(f => {
      const p = getPrice(f);
      return p === 0 || (p >= minP && p <= maxP);
    });

    switch (filters.sortBy) {
      case 'price_asc':  result.sort((a, b) => getPrice(a) - getPrice(b)); break;
      case 'price_desc': result.sort((a, b) => getPrice(b) - getPrice(a)); break;
      case 'name':       result.sort((a, b) => a.name?.localeCompare(b.name)); break;
      default: break;
    }

    setFilteredFruits(result);
    setPage(1);
  }, [fruits, searchTerm, filters]);

  useEffect(() => {
    if (searchTerm) setSearchParams({ search: searchTerm });
    else setSearchParams({});
  }, [searchTerm]);

  const handleWishlist = (id) => {
    setWishlist(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    toast.success(wishlist.includes(id) ? 'Removed from wishlist' : 'Added to wishlist');
  };

  const paginatedFruits   = filteredFruits.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const activeFilterCount = [
    filters.category !== 'all',
    filters.origin   !== 'all',
    filters.sortBy   !== 'newest',
  ].filter(Boolean).length;

  const resetFilters = () =>
    setFilters({ category: 'all', origin: 'all', sortBy: 'newest', priceRange: [0, 500] });

  // ── Filter Panel ──
  const FilterPanel = () => (
    <Box sx={{ width: isMobile ? 300 : '100%', p: isMobile ? 3 : 0 }}>
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">Filters</Typography>
          <IconButton onClick={() => setFilterDrawer(false)}><Close /></IconButton>
        </Box>
      )}

      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Category</Typography>
      <FormControl fullWidth size="small" sx={{ mb: 3 }}>
        <Select value={filters.category}
          onChange={(e) => setFilters(p => ({ ...p, category: e.target.value }))}>
          <MenuItem value="all">All Categories</MenuItem>
          {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
      </FormControl>

      {origins.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Origin</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <Select value={filters.origin}
              onChange={(e) => setFilters(p => ({ ...p, origin: e.target.value }))}>
              <MenuItem value="all">All Origins</MenuItem>
              {origins.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </Select>
          </FormControl>
        </>
      )}

      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Sort By</Typography>
      <FormControl fullWidth size="small" sx={{ mb: 3 }}>
        <Select value={filters.sortBy}
          onChange={(e) => setFilters(p => ({ ...p, sortBy: e.target.value }))}>
          <MenuItem value="newest">Newest First</MenuItem>
          <MenuItem value="price_asc">Price: Low to High</MenuItem>
          <MenuItem value="price_desc">Price: High to Low</MenuItem>
          <MenuItem value="name">Name A–Z</MenuItem>
        </Select>
      </FormControl>

      <Button fullWidth variant="outlined" onClick={resetFilters}>Reset Filters</Button>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'grey.50' }}>

      {/* Hero */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0d2f12 0%, #1b5e20 40%, #2e7d32 100%)',
        color: 'white', py: { xs: 6, md: 10 }, position: 'relative', overflow: 'hidden',
      }}>
        {[300, 500, 200].map((size, i) => (
          <Box key={i} sx={{
            position: 'absolute', borderRadius: '50%',
            backgroundColor: alpha('#4caf50', 0.07), width: size, height: size,
            top: ['-10%','30%','60%'][i], left: ['60%','-8%','75%'][i], pointerEvents: 'none',
          }} />
        ))}

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Chip label="GLOBAL FRUIT MARKETPLACE" icon={<Agriculture />}
                sx={{ bgcolor: alpha('#ffeb3b', 0.2), color: '#ffeb3b', fontWeight: 700, mb: 2 }} />
              <Typography variant="h2" fontWeight={800} sx={{ mb: 2, fontSize: { xs: '2rem', md: '3.5rem' } }}>
                Fresh Fruits from
                <Box component="span" sx={{
                  background: 'linear-gradient(90deg,#ffeb3b,#ff9800)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', ml: 1,
                }}>
                  Around the World
                </Box>
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.85, maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
                Connect directly with verified exporters. Premium quality, secure payments, global shipping.
              </Typography>
            </Box>

            <Box sx={{ maxWidth: 700, mx: 'auto' }}>
              <Paper sx={{
                p: 1.5, borderRadius: 4,
                bgcolor: alpha('#fff', 0.1), backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField fullWidth placeholder="Search fruits, varieties, origins..."
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { border: 'none' } } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: alpha('#fff', 0.7) }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchTerm('')}
                            sx={{ color: alpha('#fff', 0.7) }}>
                            <Close fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button variant="contained" sx={{
                    background: 'linear-gradient(90deg,#ffeb3b,#ff9800)',
                    color: '#1b5e20', fontWeight: 'bold', borderRadius: 3, px: 4, whiteSpace: 'nowrap',
                  }}>
                    Search
                  </Button>
                </Box>
              </Paper>
            </Box>

            <Stack direction="row" spacing={4} justifyContent="center" sx={{ mt: 3 }}>
              {[
                { icon: '🌍', label: `${fruits.length} Products` },
                { icon: '✅', label: 'Verified Sellers' },
                { icon: '🚢', label: 'Global Shipping' },
                { icon: '🔒', label: 'Secure Payments' },
              ].map((s, i) => (
                <Box key={i} sx={{ textAlign: 'center' }}>
                  <Typography variant="h5">{s.icon}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600 }}>{s.label}</Typography>
                </Box>
              ))}
            </Stack>
          </motion.div>
        </Container>
      </Box>

      {/* Main */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>

          {/* Desktop sidebar */}
          {!isMobile && (
            <Grid item md={3} lg={2.5}>
              <Paper sx={{ p: 3, borderRadius: 3, position: 'sticky', top: 88 }}
                elevation={0} variant="outlined">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">Filters</Typography>
                  {activeFilterCount > 0 && (
                    <Chip label={activeFilterCount} color="primary" size="small" />
                  )}
                </Box>
                <FilterPanel />
              </Paper>
            </Grid>
          )}

          {/* Products grid */}
          <Grid item xs={12} md={9} lg={9.5}>

            {/* Toolbar */}
            <Paper sx={{
              p: 2, mb: 3, borderRadius: 3,
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', flexWrap: 'wrap', gap: 1,
            }} elevation={0} variant="outlined">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{filteredFruits.length}</strong> of <strong>{fruits.length}</strong> fruits
                </Typography>
                {searchTerm && (
                  <Chip label={`"${searchTerm}"`} size="small"
                    onDelete={() => setSearchTerm('')} color="primary" variant="outlined" />
                )}
                {activeFilterCount > 0 && (
                  <Chip
                    label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}`}
                    size="small" onDelete={resetFilters} color="secondary" variant="outlined" />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {isMobile && (
                  <Button variant="outlined" startIcon={<FilterList />}
                    onClick={() => setFilterDrawer(true)} size="small">
                    Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                  </Button>
                )}
                <Tooltip title="Grid view">
                  <IconButton size="small" onClick={() => setViewMode('grid')}
                    color={viewMode === 'grid' ? 'primary' : 'default'}
                    sx={{ bgcolor: viewMode === 'grid' ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}>
                    <GridView />
                  </IconButton>
                </Tooltip>
                <Tooltip title="List view">
                  <IconButton size="small" onClick={() => setViewMode('list')}
                    color={viewMode === 'list' ? 'primary' : 'default'}
                    sx={{ bgcolor: viewMode === 'list' ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}>
                    <ViewList />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={fetchFruits}><Refresh /></IconButton>
                </Tooltip>
              </Box>
            </Paper>

            {/* Content */}
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <CircularProgress size={56} thickness={4} />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  Loading fresh fruits...
                </Typography>
              </Box>
            ) : filteredFruits.length === 0 ? (
              <Paper sx={{ textAlign: 'center', py: 10, borderRadius: 3 }}
                elevation={0} variant="outlined">
                <Typography variant="h1" sx={{ mb: 2 }}>🍃</Typography>
                <Typography variant="h5" fontWeight="bold" gutterBottom>No fruits found</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Try adjusting your search or filters
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button variant="outlined" onClick={() => setSearchTerm('')}>Clear Search</Button>
                  <Button variant="contained" onClick={resetFilters}>Reset Filters</Button>
                </Stack>
              </Paper>
            ) : (
              <AnimatePresence mode="wait">
                {viewMode === 'grid' ? (
                  <motion.div key="grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Grid container spacing={3}>
                      {paginatedFruits.map((fruit, idx) => (
                        <Grid item xs={12} sm={6} lg={4} key={fruit.id || idx}>
                          <FruitCard fruit={fruit} viewMode="grid"
                            onWishlist={handleWishlist}
                            isWishlisted={wishlist.includes(fruit.id)} />
                        </Grid>
                      ))}
                    </Grid>
                  </motion.div>
                ) : (
                  <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Stack spacing={2}>
                      {paginatedFruits.map((fruit, idx) => (
                        <FruitCard key={fruit.id || idx} fruit={fruit} viewMode="list"
                          onWishlist={handleWishlist}
                          isWishlisted={wishlist.includes(fruit.id)} />
                      ))}
                    </Stack>
                  </motion.div>
                )}
              </AnimatePresence>
            )}

            {/* Pagination */}
            {filteredFruits.length > itemsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Pagination
                  count={Math.ceil(filteredFruits.length / itemsPerPage)}
                  page={page}
                  onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  color="primary" size="large" showFirstButton showLastButton
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* CTA */}
      {!loading && fruits.length > 0 && (
        <Container maxWidth="xl" sx={{ pb: 8 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Paper sx={{
              background: 'linear-gradient(135deg,#1b5e20 0%,#2e7d32 100%)',
              color: 'white', p: { xs: 4, md: 6 }, borderRadius: 4, textAlign: 'center',
            }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Start Importing?
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, maxWidth: 600, mx: 'auto' }}>
                Register as a buyer and get access to verified suppliers,
                secure payments, and real-time shipment tracking.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button component={Link} to="/register" variant="contained" size="large"
                  sx={{ bgcolor: '#ffeb3b', color: '#1b5e20', fontWeight: 'bold', borderRadius: 3, px: 5 }}>
                  Register as Buyer
                </Button>
                <Button component={Link} to="/register?type=seller" variant="outlined" size="large"
                  sx={{ borderColor: alpha('#fff', 0.5), color: 'white', borderRadius: 3, px: 5 }}>
                  Become a Seller
                </Button>
              </Stack>
            </Paper>
          </motion.div>
        </Container>
      )}

      {/* Mobile filter drawer */}
      <Drawer anchor="left" open={filterDrawer} onClose={() => setFilterDrawer(false)}>
        <FilterPanel />
      </Drawer>
    </Box>
  );
};

export default Fruits;